import { FontWeights } from '@fluentui/react';
import { throwOnUndefinedColor } from '@m365-admin/customizations';
import { fadeIn, getCurrentStep, getStepIndex, lineFadeIn, SubwayNavNodeState, } from '@m365-admin/wizard';
const getTotalVisibleSteps = (steps) => {
    return steps.reduce((length, step) => step.subSteps && step.state === SubwayNavNodeState.CurrentWithSubSteps
        ? length + step.subSteps.length
        : length, steps.length);
};
const getNextStepToRender = (steps, id) => {
    return steps.reduce((nextStepNode, step) => {
        if (step.id === id) {
            if (step.subSteps && step.state === SubwayNavNodeState.CurrentWithSubSteps) {
                nextStepNode = step.subSteps[0];
            }
            else {
                nextStepNode = steps[steps.indexOf(step) + 1];
            }
        }
        else if (step.subSteps && step.state === SubwayNavNodeState.CurrentWithSubSteps) {
            const subStep = step.subSteps.filter((subStep) => subStep.id === id)[0];
            if (subStep) {
                const subStepIndex = step.subSteps.indexOf(subStep);
                if (step.subSteps.length - 1 > subStepIndex) {
                    nextStepNode = step.subSteps[subStepIndex + 1];
                }
                else {
                    nextStepNode = steps[steps.indexOf(step) + 1];
                }
            }
        }
        return nextStepNode;
    }, {});
};
const getStepToRender = (steps, id) => {
    return steps.reduce((stepNode, step) => {
        if (step.id === id) {
            stepNode = step;
        }
        else if (step.subSteps) {
            const subStep = step.subSteps.filter((subStep) => subStep.id === id)[0];
            if (subStep) {
                stepNode = subStep;
            }
        }
        return stepNode;
    }, {});
};
const getStepDetails = (steps, id, stepCompletedColor, notStartedColor) => {
    const currentStepIndexes = getStepIndex(steps, getCurrentStep(steps).id);
    const stepToRender = getStepToRender(steps, id);
    const nextStepToRender = getNextStepToRender(steps, id);
    const stepToRenderIndexes = getStepIndex(steps, stepToRender.id);
    const nextVisibleStepIsSubStep = nextStepToRender?.isSubStep ?? false;
    const { isSubStep } = stepToRender;
    let isStepAfterCurrentStep = false;
    const isStepToRenderIndexHigher = stepToRenderIndexes.stepIndex > currentStepIndexes.stepIndex &&
        !stepToRender.isSubStep;
    const isStepToRenderIndexHigherAndNoSubSteps = stepToRenderIndexes.stepIndex === currentStepIndexes.stepIndex &&
        !stepToRender.subSteps &&
        !stepToRender.isSubStep;
    const isSubstepAfterCurrentSubStep = stepToRenderIndexes.subStepIndex !== undefined &&
        currentStepIndexes.subStepIndex !== undefined &&
        stepToRenderIndexes.subStepIndex >= currentStepIndexes.subStepIndex;
    isStepAfterCurrentStep =
        isStepToRenderIndexHigher ||
            isStepToRenderIndexHigherAndNoSubSteps ||
            isSubstepAfterCurrentSubStep;
    const stepLineColor = isStepAfterCurrentStep ? notStartedColor : stepCompletedColor;
    return {
        stepToRender: stepToRender,
        stepLineColor: stepLineColor,
        isSubStep: isSubStep,
        nextVisibleStepIsSubStep: nextVisibleStepIsSubStep,
    };
};
export const getSubwayNavStyles = (props) => {
    const { steps, wizardComplete, theme, colorThrowContext } = props;
    const totalStepsVisible = getTotalVisibleSteps(steps);
    const stepCompletedColor = throwOnUndefinedColor(theme.semanticColors.stepCompleted, 'stepCompleted', 'SubwayNav', { context: colorThrowContext, theme });
    const wizardCompletedColor = throwOnUndefinedColor(theme.semanticColors.allStepsComplete, 'allStepsComplete', 'SubwayNav', { context: colorThrowContext, theme });
    const notStartedColor = throwOnUndefinedColor(theme.semanticColors.stepNotStarted, 'stepNotStarted', 'SubwayNav', { context: colorThrowContext, theme });
    return {
        root: [
            {
                MsGridColumns: '',
                gridTemplateColumns: '',
                selectors: {
                    ':before': {
                        content: '',
                        display: undefined,
                        // @ts-ignore Fabric doesn't support the MS specific grid rules
                        MsGridColumn: undefined,
                        gridColumn: undefined,
                        // @ts-ignore Fabric doesn't support the MS specific grid rules
                        MsGridRow: undefined,
                        // @ts-ignore Fabric doesn't support the MS specific grid rules
                        MsGridRowSpan: undefined,
                        gridRow: undefined,
                        backgroundColor: undefined,
                        animationName: undefined,
                        animationDuration: undefined,
                        position: undefined,
                        opacity: undefined,
                    },
                    ':after': {
                        content: undefined,
                        display: undefined,
                        // @ts-ignore Fabric doesn't support the MS specific grid rules
                        MsGridColumn: undefined,
                        gridColumn: undefined,
                        // @ts-ignore Fabric doesn't support the MS specific grid rules
                        MsGridRow: undefined,
                        // @ts-ignore Fabric doesn't support the MS specific grid rules
                        MsGridRowSpan: undefined,
                        gridRow: undefined,
                        backgroundColor: '',
                        position: undefined,
                    },
                    '& > :not([data-substep="true"])': {
                        marginBottom: '',
                    },
                    '& > [data-substep="true"]:nth-child(n)': {
                        marginBottom: '',
                    },
                    '& > [data-substep="true"]:nth-child(n) + :not([data-substep="true"]):not(:last-child)': {
                        marginTop: '',
                        marginBottom: '',
                    },
                    '& > :last-child, & > [data-substep="true"]:last-child': {
                        marginBottom: '',
                    },
                    '& > li[data-substep="true"] > button[class*="root"] div[class*="labelSelected"]': {
                        fontWeight: FontWeights.semibold,
                    },
                },
            },
        ],
        subComponentStyles: {
            item: (itemProps) => {
                const { index, id } = itemProps;
                const { nextVisibleStepIsSubStep, isSubStep, stepLineColor } = getStepDetails(steps, id, stepCompletedColor, notStartedColor);
                return {
                    root: {
                        display: 'grid',
                        animationName: nextVisibleStepIsSubStep ? lineFadeIn : fadeIn,
                        animationDelay: `${0.05 * index}s`,
                        selectors: {
                            'button[class*="root"]::before': {
                                display: 'table',
                                content: '""',
                                minHeight: index !== totalStepsVisible - 1
                                    ? nextVisibleStepIsSubStep
                                        ? '24px'
                                        : '36px'
                                    : 0,
                                maxHeight: '100%',
                                borderRadius: '2px',
                                width: '2px',
                                marginLeft: '7px',
                                backgroundColor: wizardComplete ? wizardCompletedColor : stepLineColor,
                                gridColumn: '1',
                            },
                            '& button[class*="root"]': {
                                display: 'grid',
                                gridTemplateColumns: '16px 12px minmax(0, 1fr)',
                            },
                            '& button[class*="root"] > svg[class*="iconContainer"]': {
                                gridColumn: '1',
                                gridRow: '1',
                            },
                            '& button[class*="root"] > div[class*="spacer"]': {
                                gridColumn: '2',
                                gridRow: '1 / span 2',
                            },
                            '& button[class*="root"] > div[class*="labelWrapper"]': {
                                gridColumn: '3',
                                gridRow: '1 / span 2',
                                alignContent: 'stretch',
                            },
                        },
                    },
                };
            },
        },
    };
};
//# sourceMappingURL=SubwayNav.styles.js.map